/**
 * htmlSections.ts — HTML livre transformado em SEÇÕES editáveis do Studio
 *
 * Fonte de verdade ÚNICA (mora nas Edge Functions e é reexportada para o
 * navegador em `src/lib/studio/html/section.ts`). Não depende de DOM nem de
 * biblioteca externa, para rodar igual no servidor e no editor.
 *
 * O que este módulo resolve:
 *   1. uma página inteira em HTML (normalmente gerada por uma LLM) deixa de
 *      substituir o modelo e passa a virar VÁRIOS blocos `section.html`, que o
 *      usuário move, duplica, esconde e apaga como qualquer outro bloco;
 *   2. o conteúdo (títulos, textos, links e imagens) é marcado com fichas
 *      (`@@UAIZY:chave@@`) e passa a ser editável no painel de propriedades,
 *      SEM que o usuário precise tocar no layout nem no CSS;
 *   3. o CSS interno é escopado na raiz da seção, então uma seção nunca afeta
 *      as outras, o Studio ou a página pública ao redor.
 *
 * IMPORTANTE: este módulo NÃO faz saneamento. Ele sempre recebe HTML que já
 * passou por `sanitizeStudioHtml` (a limpeza que vale é a do servidor).
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-11 v1.0.0 - Versão inicial (Studio, HTML livre editável).
 */

import { scopeStudioCss, STUDIO_RAW_HTML_ROOT } from './htmlSanitizer.ts';

/** Campo de conteúdo editável descoberto dentro de uma seção. */
export interface StudioHtmlField {
  /** Chave curta usada na ficha dentro do HTML. */
  key: string;
  /** Rótulo mostrado no painel ("Título", "Texto", "Imagem"...). */
  label: string;
  /** Natureza do campo: texto, endereço de imagem ou destino de link. */
  kind: 'text' | 'image' | 'link';
}

/** Uma seção pronta para virar bloco `section.html`. */
export interface StudioHtmlSectionData {
  /** Nome amigável sugerido ("Cabeçalho", "Herói", "Preços"...). */
  title: string;
  /** HTML da seção, já com as fichas no lugar do conteúdo editável. */
  html: string;
  /** CSS da página filtrado para o que esta seção realmente usa. */
  css: string;
  /** Campos editáveis descobertos. */
  fields: StudioHtmlField[];
  /** Valor atual de cada campo (o texto original importado). */
  content: Record<string, string>;
}

/** Resumo do fatiamento, mostrado ao usuário antes de aplicar. */
export interface StudioHtmlSplitReport {
  /** Quantidade de seções geradas. */
  sections: number;
  /** Quantidade de campos editáveis descobertos no total. */
  fields: number;
  /** Verdadeiro quando o conteúdo veio sem divisões claras (uma seção só). */
  singleSection: boolean;
}

/** Limites defensivos do fatiamento. */
export const STUDIO_HTML_SECTION_LIMITS = {
  /** Máximo de seções geradas a partir de uma página. */
  maxSections: 40,
  /** Máximo de campos editáveis por seção. */
  maxFieldsPerSection: 80,
  /** Tamanho máximo do texto aceito em um campo de texto. */
  maxFieldLength: 2000,
};

/** Tags que não possuem fechamento. */
const TAGS_VAZIAS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);

/** Tags cujo texto interno vira campo de texto editável. */
const TAGS_DE_TEXTO = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li', 'blockquote', 'figcaption']);

// =============================================================================
// Utilidades de escape
// =============================================================================

/** Escapa texto para uso como conteúdo de elemento. */
export function escaparTextoHtml(valor: unknown): string {
  return String(valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Escapa texto para uso dentro de um atributo. */
function escaparAtributoHtml(valor: unknown): string {
  return escaparTextoHtml(valor).replace(/"/g, '&quot;');
}

/**
 * Endereço aceito em `href`/`src`. Nunca deixa passar `javascript:`,
 * `vbscript:` ou `data:` que não seja imagem — mesmo critério do saneador.
 */
function enderecoSeguro(valor: string): string {
  const limpo = String(valor ?? '').trim();
  const comparavel = limpo.replace(/[\u0000-\u0020]/g, '').toLowerCase();
  if (comparavel.startsWith('javascript:') || comparavel.startsWith('vbscript:')) return '';
  if (comparavel.startsWith('data:') && !comparavel.startsWith('data:image/')) return '';
  return limpo;
}

// =============================================================================
// Leitura da árvore (sem DOM)
// =============================================================================

interface ElementoDeTopo {
  tag: string;
  attrs: string;
  outer: string;
  inner: string;
}

/** Lê o valor de um atributo dentro do texto bruto de atributos. */
function lerAtributo(attrs: string, nome: string): string {
  const re = new RegExp(`${nome}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'i');
  const achado = attrs.match(re);
  return achado ? (achado[2] ?? achado[3] ?? '') : '';
}

/**
 * Separa os elementos de primeiro nível de um trecho de HTML.
 * Texto solto entre elementos é anexado ao elemento anterior, para não sumir.
 */
function elementosDeTopo(html: string): ElementoDeTopo[] {
  const saida: ElementoDeTopo[] = [];
  const re = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)\b([^>]*)>/g;

  let profundidade = 0;
  let inicio = -1;
  let tagAtual = '';
  let attrsAtual = '';
  let inicioInterno = -1;
  let m: RegExpExecArray | null;

  while ((m = re.exec(html)) !== null) {
    const fechamento = m[1] === '/';
    const tag = m[2].toLowerCase();
    const attrs = m[3] ?? '';
    const autoFechada = TAGS_VAZIAS.has(tag) || /\/\s*$/.test(attrs);

    if (!fechamento) {
      if (profundidade === 0) {
        if (autoFechada) {
          // Elemento sozinho (ex.: <img>) também conta como seção candidata.
          saida.push({ tag, attrs, outer: m[0], inner: '' });
          continue;
        }
        inicio = m.index;
        inicioInterno = re.lastIndex;
        tagAtual = tag;
        attrsAtual = attrs;
        profundidade = 1;
      } else if (!autoFechada) {
        profundidade += 1;
      }
      continue;
    }

    if (profundidade === 0) continue;
    profundidade -= 1;
    if (profundidade === 0 && tag === tagAtual && inicio >= 0) {
      saida.push({
        tag: tagAtual,
        attrs: attrsAtual,
        outer: html.slice(inicio, re.lastIndex),
        inner: html.slice(inicioInterno, m.index),
      });
      inicio = -1;
    }
  }

  return saida;
}

/** Conta quantos elementos existem no primeiro nível de um trecho. */
function quantidadeDeFilhos(html: string): number {
  return elementosDeTopo(html).length;
}

// =============================================================================
// Nome amigável da seção
// =============================================================================

/** Palavras-chave que ajudam a nomear a seção pelo id/classe. */
const PISTAS: Array<[RegExp, string]> = [
  [/nav|menu|topo|header/i, 'Cabeçalho'],
  [/hero|capa|banner|intro/i, 'Destaque'],
  [/pric|plan|preco|preço|tabela/i, 'Preços'],
  [/faq|duvida|dúvida|perguntas/i, 'Perguntas frequentes'],
  [/depoim|testimon|review/i, 'Depoimentos'],
  [/contato|contact|form/i, 'Contato'],
  [/rodape|rodapé|footer/i, 'Rodapé'],
  [/sobre|about|quem/i, 'Sobre'],
  [/benef|feature|recurso|vantag/i, 'Benefícios'],
  [/cta|acao|ação|comec|começ/i, 'Chamada para ação'],
  [/gale|gallery|foto/i, 'Galeria'],
  [/numer|númer|stat|result/i, 'Números'],
  [/video|vídeo/i, 'Vídeo'],
  [/passo|step|como/i, 'Etapas'],
];

/** Sugere um nome legível para a seção. */
function nomeDaSecao(elemento: ElementoDeTopo, indice: number, total: number): string {
  const referencia = `${lerAtributo(elemento.attrs, 'id')} ${lerAtributo(elemento.attrs, 'class')}`;

  if (elemento.tag === 'header') return 'Cabeçalho';
  if (elemento.tag === 'footer') return 'Rodapé';
  if (elemento.tag === 'nav') return 'Menu do topo';

  for (const [padrao, nome] of PISTAS) {
    if (padrao.test(referencia)) return nome;
  }

  // Sem pista no código: usa o primeiro título do conteúdo.
  const titulo = elemento.inner.match(/<h[1-3][^>]*>([^<]{3,60})<\/h[1-3]>/i);
  if (titulo) return titulo[1].trim();

  if (indice === 0) return 'Destaque';
  if (indice === total - 1) return 'Rodapé';
  return `Seção ${indice + 1}`;
}

// =============================================================================
// Campos editáveis
// =============================================================================

/** Rótulo do campo conforme a tag de origem. */
function rotuloDoCampo(tag: string, ordem: number): string {
  if (/^h[12]$/.test(tag)) return `Título ${ordem}`;
  if (/^h[3-6]$/.test(tag)) return `Subtítulo ${ordem}`;
  if (tag === 'a') return `Botão/link ${ordem}`;
  if (tag === 'img') return `Imagem ${ordem}`;
  return `Texto ${ordem}`;
}

/**
 * Substitui o conteúdo editável por fichas e devolve os campos encontrados.
 * Só o que é claramente conteúdo entra: título, parágrafo, item de lista,
 * texto e destino de link, endereço e descrição de imagem.
 */
function extrairCampos(
  html: string,
  prefixo: string,
): { html: string; fields: StudioHtmlField[]; content: Record<string, string> } {
  const fields: StudioHtmlField[] = [];
  const content: Record<string, string> = {};
  let sequencia = 0;

  const registrar = (label: string, kind: StudioHtmlField['kind'], valor: string): string | null => {
    if (fields.length >= STUDIO_HTML_SECTION_LIMITS.maxFieldsPerSection) return null;
    sequencia += 1;
    const key = `${prefixo}${sequencia}`;
    fields.push({ key, label, kind });
    content[key] = valor.slice(0, STUDIO_HTML_SECTION_LIMITS.maxFieldLength);
    return `@@UAIZY:${key}@@`;
  };

  let saida = html;

  // 1) Imagens: endereço e descrição.
  let ordemImagem = 0;
  saida = saida.replace(/<img\b([^>]*)>/gi, (todo, attrs: string) => {
    const src = lerAtributo(attrs, 'src');
    if (!src || src.includes('@@UAIZY:')) return todo;
    ordemImagem += 1;
    const ficha = registrar(rotuloDoCampo('img', ordemImagem), 'image', src);
    if (!ficha) return todo;
    const novosAttrs = attrs.replace(
      /src\s*=\s*("([^"]*)"|'([^']*)')/i,
      `src="${ficha}"`,
    );
    return `<img${novosAttrs}>`;
  });

  // 2) Links: texto visível e destino.
  let ordemLink = 0;
  saida = saida.replace(
    /<a\b([^>]*)>([^<]{1,300})<\/a\s*>/gi,
    (todo, attrs: string, texto: string) => {
      if (!texto.trim() || texto.includes('@@UAIZY:')) return todo;
      ordemLink += 1;
      const fichaTexto = registrar(rotuloDoCampo('a', ordemLink), 'text', texto.trim());
      if (!fichaTexto) return todo;
      const href = lerAtributo(attrs, 'href');
      let novosAttrs = attrs;
      if (href) {
        const fichaHref = registrar(`Destino do botão/link ${ordemLink}`, 'link', href);
        if (fichaHref) {
          novosAttrs = attrs.replace(/href\s*=\s*("([^"]*)"|'([^']*)')/i, `href="${fichaHref}"`);
        }
      }
      return `<a${novosAttrs}>${fichaTexto}</a>`;
    },
  );

  // 3) Títulos, parágrafos e itens de lista com texto simples.
  let ordemTexto = 0;
  saida = saida.replace(
    /<(h1|h2|h3|h4|h5|h6|p|li|blockquote|figcaption)\b([^>]*)>([^<]{1,600})<\/\1\s*>/gi,
    (todo, tag: string, attrs: string, texto: string) => {
      const minuscula = tag.toLowerCase();
      if (!TAGS_DE_TEXTO.has(minuscula)) return todo;
      if (!texto.trim() || texto.includes('@@UAIZY:')) return todo;
      ordemTexto += 1;
      const ficha = registrar(rotuloDoCampo(minuscula, ordemTexto), 'text', texto.trim());
      if (!ficha) return todo;
      return `<${tag}${attrs}>${ficha}</${tag}>`;
    },
  );

  return { html: saida, fields, content };
}

// =============================================================================
// CSS por seção
// =============================================================================

/** Diz se um seletor usa alguma classe/id ausente do HTML da seção. */
function seletorUsadoNaSecao(seletor: string, html: string): boolean {
  // A raiz escopada existe em TODAS as seções: não conta como pista de uso.
  const tokens = (seletor.match(/[.#][A-Za-z0-9_-]+/g) ?? []).filter(
    (token) => token !== `.${STUDIO_RAW_HTML_ROOT}`,
  );
  // Sem classe nem id (ex.: `h1`, ou a própria raiz): mantém.
  if (!tokens.length) return true;
  return tokens.some((token) => {
    const nome = token.slice(1);
    if (token.startsWith('#')) return new RegExp(`id\\s*=\\s*["'][^"']*\\b${nome}\\b`).test(html);
    return new RegExp(`class\\s*=\\s*["'][^"']*\\b${nome}\\b`).test(html);
  });
}

/**
 * Mantém apenas as regras de CSS que a seção realmente usa.
 * Evita repetir a folha inteira em cada uma das seções da página.
 */
function filtrarCssPorUso(css: string, html: string): string {
  if (!css.trim()) return '';
  const saida: string[] = [];
  const re = /([^{}]+)\{([^{}]*)\}/g;
  let m: RegExpExecArray | null;

  while ((m = re.exec(css)) !== null) {
    const seletor = m[1].trim();
    // Blocos condicionais (`@media`, `@supports`, `@font-face`) são mantidos.
    if (seletor.startsWith('@') || /}\s*$/.test(seletor)) {
      saida.push(m[0]);
      continue;
    }
    const partes = seletor.split(',').filter((parte) => seletorUsadoNaSecao(parte, html));
    if (partes.length) saida.push(`${partes.join(',')}{${m[2]}}`);
  }

  return saida.join('\n');
}

// =============================================================================
// Fatiamento
// =============================================================================

/**
 * Divide uma página em HTML (já saneada) nas seções que virarão blocos.
 * Quando não há divisões claras, devolve uma seção única e avisa no relatório.
 */
export function splitStudioHtmlSections(
  htmlSaneado: string,
): { sections: StudioHtmlSectionData[]; report: StudioHtmlSplitReport } {
  let html = String(htmlSaneado ?? '');

  // 1) Estilos internos: extraídos e escopados na raiz do conteúdo importado.
  const estilos: string[] = [];
  html = html.replace(/<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi, (_todo, css: string) => {
    estilos.push(scopeStudioCss(css));
    return '';
  });
  const cssCompleto = estilos.filter((c) => c.trim()).join('\n');

  // 2) Estrutura de página completa vira apenas o conteúdo do corpo.
  const corpo = html.match(/<body\b[^>]*>([\s\S]*?)<\/body\s*>/i);
  if (corpo) html = corpo[1];
  html = html.replace(/<\/?(html|head|body|meta|title)\b[^>]*>/gi, '').trim();

  // 3) Elementos de primeiro nível: cada um vira uma seção.
  let elementos = elementosDeTopo(html);

  // Página inteira dentro de um único invólucro: desce UM nível e repete a
  // classe do invólucro em cada seção, para o CSS continuar valendo.
  let invólucro = '';
  if (elementos.length === 1 && quantidadeDeFilhos(elementos[0].inner) >= 2) {
    invólucro = lerAtributo(elementos[0].attrs, 'class');
    elementos = elementosDeTopo(elementos[0].inner);
  }

  if (!elementos.length) {
    const conteudo = html.trim();
    if (!conteudo) return { sections: [], report: { sections: 0, fields: 0, singleSection: true } };
    elementos = [{ tag: 'div', attrs: '', outer: `<div>${conteudo}</div>`, inner: conteudo }];
  }

  const limitados = elementos.slice(0, STUDIO_HTML_SECTION_LIMITS.maxSections);
  const sections: StudioHtmlSectionData[] = [];
  let totalCampos = 0;

  limitados.forEach((elemento, indice) => {
    const corpoSecao = invólucro
      ? `<div class="${escaparAtributoHtml(invólucro)}">${elemento.outer}</div>`
      : elemento.outer;

    const extraido = extrairCampos(corpoSecao, `c${indice + 1}_`);
    totalCampos += extraido.fields.length;

    sections.push({
      title: nomeDaSecao(elemento, indice, limitados.length),
      html: extraido.html,
      css: filtrarCssPorUso(cssCompleto, corpoSecao),
      fields: extraido.fields,
      content: extraido.content,
    });
  });

  return {
    sections,
    report: {
      sections: sections.length,
      fields: totalCampos,
      singleSection: sections.length <= 1,
    },
  };
}

// =============================================================================
// Renderização da seção
// =============================================================================

/** Modo de saída considerado ao desenhar a seção. */
export type StudioHtmlSectionMode = 'editor' | 'published' | 'thumbnail' | 'pdf' | 'email';

/** Propriedades do bloco `section.html`. */
export interface StudioHtmlSectionProps {
  html?: unknown;
  css?: unknown;
  fields?: unknown;
  content?: unknown;
  bleed?: unknown;
  title?: unknown;
}

/** Lê a lista de campos com segurança. */
export function lerCamposDaSecao(valor: unknown): StudioHtmlField[] {
  if (!Array.isArray(valor)) return [];
  return valor
    .filter((item) => item && typeof item === 'object')
    .map((item) => item as Record<string, unknown>)
    .filter((item) => typeof item.key === 'string' && item.key)
    .map((item) => ({
      key: String(item.key),
      label: typeof item.label === 'string' ? item.label : 'Conteúdo',
      kind:
        item.kind === 'image' || item.kind === 'link'
          ? (item.kind as StudioHtmlField['kind'])
          : 'text',
    }));
}

/**
 * Ajustes de impressão: posição fixa/grudada não existe em papel e vira fluxo
 * normal; a animação também é descartada.
 */
function ajustarParaImpressao(texto: string): string {
  return texto
    .replace(/position\s*:\s*(fixed|sticky)/gi, 'position:static')
    .replace(/animation\s*:[^;"']+;?/gi, '');
}

/**
 * Monta o HTML final da seção: fichas trocadas pelos valores atuais, CSS
 * escopado e raiz isolada. É a MESMA função usada pelo editor, pela página
 * publicada e pelo PDF.
 */
export function renderStudioHtmlSection(
  props: StudioHtmlSectionProps,
  modo: StudioHtmlSectionMode = 'published',
): string {
  // O bloco não existe em e-mail: nada é impresso (o Studio avisa antes).
  if (modo === 'email') return '';

  const campos = lerCamposDaSecao(props.fields);
  const conteudo = (props.content && typeof props.content === 'object'
    ? props.content
    : {}) as Record<string, unknown>;

  let html = String(props.html ?? '');
  let css = String(props.css ?? '');

  for (const campo of campos) {
    const bruto = conteudo[campo.key];
    const valor = bruto == null ? '' : String(bruto);
    const substituto =
      campo.kind === 'text'
        ? escaparTextoHtml(valor)
        : escaparAtributoHtml(enderecoSeguro(valor));
    html = html.split(`@@UAIZY:${campo.key}@@`).join(substituto);
  }

  // Ficha sem campo correspondente (conteúdo editado à mão) nunca vaza.
  html = html.replace(/@@UAIZY:[A-Za-z0-9_]+@@/g, '');

  if (modo === 'pdf') {
    html = ajustarParaImpressao(html);
    css = ajustarParaImpressao(css);
  }

  const contida = String(props.bleed ?? 'full') === 'contained';

  return `<section class="st-sec${contida ? ' st-contained' : ''}">${
    css ? `<style>${css}</style>` : ''
  }<div class="${STUDIO_RAW_HTML_ROOT}">${html}</div></section>`;
}
